/** Withholdings (удержания и штрафы) — payroll deductions. */

export const DEFAULT_KIND_DEFS = [
  { key: "penalty", name: "Дисциплинарный штраф", limit_pct: 20, enabled: true, builtin: true },
  { key: "damage", name: "Возмещение ущерба", limit_pct: 20, enabled: true, builtin: true },
  { key: "loan", name: "Возврат займа", limit_pct: 20, enabled: true, builtin: true },
  { key: "alimony", name: "Алименты", limit_pct: 50, enabled: true, builtin: true },
  { key: "advance", name: "Переплата / аванс", limit_pct: 20, enabled: true, builtin: true },
  { key: "union", name: "Профвзносы", limit_pct: 20, enabled: true, builtin: true },
  { key: "other", name: "Прочее", limit_pct: 20, enabled: true, builtin: true },
];

const BUILTIN_KEYS = new Set(DEFAULT_KIND_DEFS.map((d) => d.key));

/** All kinds including disabled (settings UI). */
export function allWithholdingKindDefs(company) {
  const overrides = {};
  for (const d of company?.withholding_kind_defs || []) {
    const key = (d.key || "").trim();
    if (key) overrides[key] = d;
  }
  const out = DEFAULT_KIND_DEFS.map((base) => {
    const ov = overrides[base.key] || {};
    return {
      key: base.key,
      name: (ov.name || base.name).trim() || base.name,
      limit_pct: ov.limit_pct != null ? Number(ov.limit_pct) : base.limit_pct,
      enabled: "enabled" in ov ? ov.enabled !== false : base.enabled,
      builtin: true,
    };
  });
  for (const [key, ov] of Object.entries(overrides)) {
    if (BUILTIN_KEYS.has(key)) continue;
    const name = (ov.name || key).trim();
    if (!name) continue;
    out.push({
      key,
      name,
      limit_pct: Number(ov.limit_pct) || 20,
      enabled: ov.enabled !== false,
      builtin: false,
    });
  }
  return out;
}

/** Enabled kinds for selects. */
export function enabledWithholdingKindDefs(company) {
  return allWithholdingKindDefs(company).filter((k) => k.enabled !== false);
}

export function kindLimitPct(company, kind) {
  const found = allWithholdingKindDefs(company).find((k) => k.key === kind);
  if (found) return found.limit_pct;
  return kind === "alimony" ? 50 : 20;
}

export function kindLabel(kind, company, t) {
  const found = allWithholdingKindDefs(company).find((k) => k.key === kind);
  if (found?.name) return found.name;
  const key = `withholdings.kind_${kind}`;
  return t(key, kind);
}

export function computeWithholdingAmount(item, monthlySalary, baseGross = 0) {
  const mode = item?.amount_mode || "fixed";
  if (mode === "fixed") return Math.max(0, Number(item.amount) || 0);
  if (mode === "percent_salary") {
    return Math.max(0, (Number(monthlySalary) || 0) * ((Number(item.amount) || 0) / 100));
  }
  if (mode === "percent_gross") {
    return Math.max(0, (Number(baseGross) || 0) * ((Number(item.amount) || 0) / 100));
  }
  return 0;
}

/** @returns {{ preTaxTotal, postTaxTotal, total, detail }} */
export function aggregateWithholdings(items, monthlySalary, baseGross) {
  let preTaxTotal = 0;
  let postTaxTotal = 0;
  const detail = [];
  for (const it of items || []) {
    if (it.status === "cancelled") continue;
    const amt = computeWithholdingAmount(it, monthlySalary, baseGross);
    if (!amt) continue;
    if (it.tax_timing === "pre_tax") preTaxTotal += amt;
    else postTaxTotal += amt;
    detail.push({ ...it, computed: amt });
  }
  return {
    preTaxTotal,
    postTaxTotal,
    total: preTaxTotal + postTaxTotal,
    detail,
  };
}

/** Legal limit hints (non-blocking). Uses company kind defs when provided. */
export function withholdingLimitWarning(totalWithholding, netBeforeWithholding, items, company = null) {
  if (!totalWithholding || !netBeforeWithholding) return null;
  const active = (items || []).filter((i) => i.status !== "cancelled");
  let limitPct = 20;
  if (company) {
    limitPct = Math.max(...active.map((i) => kindLimitPct(company, i.kind)), 20);
  } else {
    const hasAlimony = active.some((i) => i.kind === "alimony");
    limitPct = hasAlimony ? 50 : 20;
  }
  const pct = (totalWithholding / netBeforeWithholding) * 100;
  if (pct > limitPct) {
    return { limitPct, pct, hasAlimony: active.some((i) => i.kind === "alimony") };
  }
  return null;
}

export function monthBounds(year, month0) {
  const mStart = `${year}-${String(month0 + 1).padStart(2, "0")}-01`;
  const ny = month0 === 11 ? year + 1 : year;
  const nm = month0 === 11 ? 0 : month0 + 1;
  const mNextStart = `${ny}-${String(nm + 1).padStart(2, "0")}-01`;
  return { mStart, mNextStart };
}

export function overlapsMonth(validFrom, validTo, year, month0) {
  const { mStart, mNextStart } = monthBounds(year, month0);
  const vf = validFrom || "0000-01-01";
  const vt = validTo || "9999-12-31";
  return vt >= mStart && vf < mNextStart;
}

export function activeRecurringInMonth(recurring, year, month0) {
  return (recurring || []).filter(
    (r) => r.status === "active" && overlapsMonth(r.valid_from, r.valid_to, year, month0),
  );
}

export function recurringAsWithholding(r) {
  return {
    withholding_id: `rwh:${r.recurring_withholding_id}`,
    recurring_withholding_id: r.recurring_withholding_id,
    user_id: r.user_id,
    kind: r.kind || "other",
    name: r.name,
    amount_mode: r.amount_mode || "fixed",
    amount: r.amount,
    tax_timing: r.tax_timing || "post_tax",
    status: r.status === "active" ? "approved" : "cancelled",
    basis: r.basis || "",
    is_recurring: true,
  };
}

export function mergeWithholdingsForMonth(oneShot, recurring, year, month0) {
  const virtual = activeRecurringInMonth(recurring, year, month0).map(recurringAsWithholding);
  return [...(oneShot || []), ...virtual];
}
